'use client';

import React, { ReactNode } from 'react';
import { cn } from '@ecom/helper-react-class-combine';
import { useCarouselContext } from '../carousel-context';

interface DotRenderProps {
  index: number;
  isActive: boolean;
  id: string;
  controls: string;
  label: string;
  onClick: () => void;
}

interface DotsProps {
  className?: string;
  dotClassName?: string;
  activeDotClassName?: string;
  /** Full control over a single dot's markup, for portals that need it. */
  renderDot?: (props: DotRenderProps) => ReactNode;
}

export function CarouselDots({
  className,
  dotClassName,
  activeDotClassName,
  renderDot,
}: DotsProps): React.ReactElement | null {
  const { slideCount, selectedIndex, scrollTo, labels, getSlideId, getTabId } =
    useCarouselContext();
  if (slideCount <= 1) return null;

  return (
    <div className={cn('flex w-full items-center justify-center gap-4', className)} role='tablist'>
      {Array.from({ length: slideCount }, (_, index) => {
        const isActive = selectedIndex === index;
        const dotProps: DotRenderProps = {
          index,
          isActive,
          id: getTabId(index),
          controls: getSlideId(index),
          label: `${labels.goToSlide} ${index + 1}`,
          onClick: () => scrollTo(index),
        };

        if (renderDot) {
          return <React.Fragment key={index}>{renderDot(dotProps)}</React.Fragment>;
        }

        return (
          <button
            key={index}
            id={dotProps.id}
            type='button'
            role='tab'
            aria-controls={dotProps.controls}
            aria-label={dotProps.label}
            aria-selected={isActive}
            onClick={dotProps.onClick}
            className={cn(
              'h-5 w-5 rounded-full border border-solid border-[#b3b3b3] hover:bg-[#b3b3b3]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current',
              dotClassName,
              isActive && cn('border-primary bg-primary hover:bg-primary', activeDotClassName),
            )}
          />
        );
      })}
    </div>
  );
}
